import React from 'react';
import { useTranslation } from '@shared/core';
import { pxToRem } from '@withjoy/joykit/theme';
import { Flex, Box } from '@withjoy/joykit';
import { StarRating } from '@shared/components';
import { Maybe } from '@graphql/generated';

/**
 * Returns localized tooltip content for hotel star ratings.
 *
 * @param level - The star rating level (1-5). Values outside this range default to 5-star content.
 * @param tq - A tagged template function for translating strings, typically from an i18n library.
 * @returns An object containing a translated `title` and `description` for the given star rating level.
 *
 * @example
 * ```ts
 * const content = getStarRatingTooltipContent(3, t);
 * // Returns: { title: "3 Stars — Well‑Rounded & Convenient", description: "A balanced stay..." }
 * ```
 */ function getStarRatingTooltipContent(level: number, tq: (strings: TemplateStringsArray, ...values: unknown[]) => string) {
  switch (level) {
    case 1:
      return {
        title: tq`Simple & Budget-Friendly`,
        description: tq`For travelers who value savings and just need the essentials—clean, no-frills stays close to where you need to be.`,
      };
    case 2:
      return {
        title: tq`Comfort Meets Affordability`,
        description: tq`Reliable comfort with helpful amenities like free Wi‑Fi or breakfast. Great for quick getaways or overnight stays.`,
      };
    case 3:
      return {
        title: tq`Well‑Rounded & Convenient`,
        description: tq`A balanced stay with thoughtful service, comfortable rooms, and extra perks like on‑site dining or a fitness center.`,
      };
    case 4:
      return {
        title: tq`Stylish & Full‑Service`,
        description: tq`Upscale comfort with modern design, excellent service, and amenities like spas, restaurants, or concierge support.`,
      };
    case 5:
    default:
      return {
        title: tq`Luxury Without Limits`,
        description: tq`World‑class hospitality, stunning interiors, and every detail considered—from gourmet dining to personalized experiences.`,
      };
  }
}

/**
 * Props for the StarRatingWithTooltip component.
 * @property starRating - The current hotel star rating (optional).
 */
export type StarRatingWithTooltipProps = {
  readonly starRating?: Maybe<number>;
};

export const StarRatingWithTooltip = ({ starRating }: StarRatingWithTooltipProps) => {
  const { tq } = useTranslation('travelMap');

  if (starRating == null) return null;

  // Clamp the star rating to an integer between 1 and 5
  const nearestLevel = Math.max(1, Math.min(5, Math.round(Number(starRating))));

  const content = getStarRatingTooltipContent(nearestLevel, tq);

  return (
    <StarRating
      id={`star-rating-hotel-form-${nearestLevel}`}
      hideNonFilledStars
      showPartialRating
      rating={starRating || 0}
      showTooltip={true}
      toolTipContent={
        <Flex maxWidth={pxToRem(256)}>
          <Box typographyVariant="label3">
            {content.title}
            <Box
              typographyVariant="label2"
              color="mono4"
              display="block"
              marginTop={2}
            >
              {content.description}
            </Box>
          </Box>
        </Flex>
      }
    />
  );
};
